import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController, RadioGroupChangeEventDetail } from '@ionic/angular';
import { firstValueFrom, forkJoin } from 'rxjs';
import { Category, Tag } from 'src/app/interfaces/api/category';
import { ProductService } from 'src/app/services/product.service';
import { ActiveLink, CategorySidebarService, SidebarLink } from './service/category-sidebar.service';
import { City } from 'src/app/interfaces/city';
import { Preferences } from '@capacitor/preferences';

@Component({
  selector: 'app-category-sidebar-wrapper',
  templateUrl: './category-sidebar-wrapper.component.html',
  styleUrls: ['./category-sidebar-wrapper.component.scss'],
})
export class CategorySidebarWrapperComponent implements OnInit {
  @Input() mode: 'single' | 'multiple' = 'single';
  @Output() selectedCityChange = new EventEmitter<City>();
  @Output() selectedCategoriesChange = new EventEmitter<SidebarLink[]>();
  @Output() selectedTagsChange = new EventEmitter<number[]>();
  currentCategoryId: number;
  categories: Category[] = [];
  sidebarLinks: SidebarLink[] = [];
  activeLinkIds: ActiveLink[];
  cities: City[];
  selectedCity: City;
  hasSelectedCity: City;
  selectedCategories: SidebarLink[] = [];
  activeTags: number[] = [];
  tags: Tag[] = [];
  transformedTags: { type: string; tags: Tag[] }[] = [];

  hideLocationSection: boolean;
  hideTagsSection = true;

  constructor(
    private productService: ProductService,
    private navController: NavController,
    private route: ActivatedRoute,
    private categorySidebarService: CategorySidebarService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(async paramMap => {
      if (paramMap.has('subcategoryId')) {
        this.currentCategoryId = Number(paramMap.get('subcategoryId'));
        this.hideLocationSection = false;
        this.hideTagsSection = false;
      } else {
        this.hideTagsSection = true;
      }
      if (paramMap.has('categoryId')) {
        this.currentCategoryId = Number(paramMap.get('categoryId'));
        this.hideLocationSection = true;
      }
      if (this.sidebarLinks?.length > 0) {
        this.activeLinkIds = this.categorySidebarService.findActiveLinks(this.sidebarLinks, this.currentCategoryId);
        this.categorySidebarService.setActiveLinkIds(this.activeLinkIds);
      }
      this.hasSelectedCity = JSON.parse((await Preferences.get({ key: 'selectedCity' })).value);
      this.selectedCity = this.hasSelectedCity;
      this.loadData();
      // console.log('category-sidebar-wrapper ngoninit', this.currentCategoryId);
    });
  }

  checkCategoryHandler(event: { checked: boolean; sidebarLink: SidebarLink }) {
    if (event.checked) {
      this.selectedCategories.push(event.sidebarLink);
    } else {
      this.selectedCategories = this.selectedCategories.filter(category => category.id !== event.sidebarLink.id);
    }

    this.selectedCategoriesChange.emit(this.selectedCategories);
  }

  async loadData() {
    const citiesReq = this.productService.getWarehouseCities();
    this.cities = (await firstValueFrom(citiesReq)).cities;

    this.categorySidebarService.sidebarLinks$.subscribe(sidebarLinks => {
      this.sidebarLinks = sidebarLinks;
    });

    this.categorySidebarService.tags$.subscribe(tags => {
      this.tags = tags;
      this.transformedTags = this.transformTags(this.tags);
    });

    if (this.sidebarLinks?.length === 0) {
      const categoriesReq = this.productService.getCategories();
      this.categories = (await firstValueFrom(categoriesReq)).categories;

      const loadedSidebarLinks: SidebarLink[] = [];

      for (const category of this.categories) {
        loadedSidebarLinks.push({
          id: category.id,
          name: category.name,
          tags: category.tags,
          children: category.has_children ? await this.getSubcategories(category.id) : [],
        });
      }

      this.sidebarLinks = loadedSidebarLinks;
      this.categorySidebarService.setSidebarLinks(loadedSidebarLinks);
    }

    this.activeLinkIds = this.categorySidebarService.findActiveLinks(this.sidebarLinks, this.currentCategoryId);
    this.categorySidebarService.setActiveLinkIds(this.activeLinkIds);
  }

  async getSubcategories(parentId: number) {
    const childrenReq = this.productService.getSubCategories(parentId);
    const subcategories = (await firstValueFrom(childrenReq)).categories;
    const subcategoryLinks: SidebarLink[] = [];

    for (const subcategory of subcategories) {
      subcategoryLinks.push({
        id: subcategory.id,
        name: subcategory.name,
        tags: subcategory.tags,
        children: subcategory.has_children ? await this.getSubcategories(subcategory.id) : [],
      });
    }

    return subcategoryLinks;
  }

  async radioChangeHandler(event: Event) {
    const customEvent = event as CustomEvent<RadioGroupChangeEventDetail>;
    const selectedId = customEvent.detail.value;

    if (selectedId === this.selectedCity?.id) {
      return;
    }

    this.selectedCity = this.cities.find(city => city.id === selectedId);
    await Preferences.set({ key: 'selectedCity', value: JSON.stringify(this.selectedCity) });
    this.selectedCityChange.emit(this.selectedCity);
  }

  async navigateToCategory(categoryLink: SidebarLink) {
    await this.navController.navigateForward(`/category-detail/${categoryLink.id}`);
  }

  async navigateToSubCategory(subcategoryLink: SidebarLink) {
    await this.navController.navigateForward(`/subcategory-detail/${subcategoryLink.id}`, {
      state: {
        data: {
          category_name: subcategoryLink.name,
          tags: subcategoryLink.tags,
        },
      },
    });
  }

  transformTags(tags: Tag[]) {
    const transformedTags: { type: string; tags: Tag[] }[] = [];

    for (const tag of tags) {
      const existingTypeIndex = transformedTags.findIndex(t => t.type === tag.type);

      if (existingTypeIndex === -1) {
        transformedTags.push({ type: tag.type, tags: [tag] });
      } else {
        transformedTags[existingTypeIndex].tags.push(tag);
      }
    }

    return transformedTags;
  }

  isActiveTag(tagId: number) {
    const isTagExist = this.activeTags.findIndex(t => t === tagId) !== -1;
    return isTagExist;
  }

  setActiveTags(tagId: number) {
    const tagIndex = this.activeTags.indexOf(tagId);

    if (tagIndex !== -1) {
      this.activeTags.splice(tagIndex, 1);
    } else {
      this.activeTags.push(tagId);
    }

    this.selectedTagsChange.emit(this.activeTags);
  }
}
